import { getApiUrl } from '@/lib/api-config';
import { withIdempotentRetry } from '@/lib/idempotent-fetch';
import type { AiBidComparison, BidComparisonInput } from '../types';

/**
 * Every AI call of the Bids module goes through the backend's POST /api/gpt proxy (auth, rate limit, credits,
 * idempotency). The OpenAI key and the prompts that matter live on the server.
 */
async function postGpt(body: Record<string, unknown>): Promise<any> {
  const response = await withIdempotentRetry(getApiUrl('api/gpt'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const parsed = await response.json().catch(() => ({}));
  // the AI gates (credits, concurrency, rate limits) answer { error: <code>, message: <for the user> }; other failures { error: <message> }
  if (!response.ok) throw new Error(parsed.message || parsed.error || 'The AI request failed. Please try again.');
  return parsed;
}

/** A plain prompt that must answer with a small JSON object (extraction). Returns the model's text. */
export async function requestGptJson(prompt: string): Promise<string> {
  return (await postGpt({ prompt })).content || '';
}

/**
 * Compares 2+ bid documents. The server asks the model for a schema-validated answer and normalises it: the result is
 * ready to render, or this throws with a message fit for the user (incomplete AI answer, no credits, ...).
 */
export async function compareBidDocuments(bids: BidComparisonInput[]): Promise<AiBidComparison> {
  const { result } = await postGpt({
    purpose: 'bid_comparison',
    bids: bids.map((bid) => ({ name_hint: bid.nameHint, text: bid.text, known_total: bid.knownTotal ?? undefined })),
  });
  if (!result) throw new Error('The AI returned no comparison. Please try again.');
  return result as AiBidComparison;
}
